import {type Properties, type PropertyType} from './Properties.ts';
import {KubernetesConfig} from './KubernetesConfig.ts';
import {
    DefaultDiscoveryConfig,
    type DiscoveryConfig
} from './DiscoveryConfig.ts';

export class AutoDetectionConfig {
    enabled: boolean = true;
}

export interface MulticastConfig {
    readonly group: string
    readonly port: number
}

class DefaultMulticastConfig implements MulticastConfig {
    enabled: boolean = false;
    group: string = '239.1.2.3';
    port: number = 34567;
}

export interface TcpIpConfig {
    members: string[]
}

class DefaultTcpIpConfig implements TcpIpConfig {
    enabled: boolean = false;
    members: string[] = [];
}

export class JoinConfig {
    private _multicast = new DefaultMulticastConfig();
    private _tcpIp = new DefaultTcpIpConfig();
    private _kubernetes = new KubernetesConfig();
    private _discovery = new DefaultDiscoveryConfig();
    private _autoDetection = new AutoDetectionConfig();

    get discovery(): DiscoveryConfig {
        return this._discovery;
    }

    get multicast(): MulticastConfig {
        return this._multicast;
    }

    get autoDetectionEnabled(): boolean {
        return this._autoDetection.enabled
            && !this._multicast.enabled
            && !this._tcpIp.enabled
            && !this._kubernetes.enabled
            && !this._discovery.enabled;
    }
}

export class NetworkConfig {
    public static readonly DEFAULT_PORT: number = 8383;
    port: number = NetworkConfig.DEFAULT_PORT;
    reuseAddress: boolean;
    publicAddress?: string;
    join = new JoinConfig();

    constructor() {
        this.reuseAddress = process.platform !== "win32";
    }
}

export class CorsConfig {
    #allowOrigin?: '*' | string[] = undefined;
    set allowOrigin(value: string | undefined) {
        if (value !== undefined) {
            if (value === '*') {
                this.#allowOrigin = value;
            } else if (Array.isArray(value)) {
                this.#allowOrigin = value;
            } else {
                this.#allowOrigin = value.split(',')
            }
        }
    }

    get allowOrigin(): '*' | string[] | undefined {
        return this.#allowOrigin;
    }

    allowCredentials: boolean = false;
}

export class ServerConfig {
    port?: number = undefined;
    host?: string = undefined;
    cors = new CorsConfig();
    auth: {
        type: 'none' | 'basic' | 'oauth2',
        basic?: {
            realm?: string
        }
    } = {
        type: 'none',
        basic: {
            realm: 'io.Bridge'
        }
    };
    wsPingInterval?: number = 30000; // 30 seconds
}

export class MeshConfig {
    timeout?: number
}

export class GatewayConfig {
    enabled?: boolean;
    contexts: {
        lifetime: 'retained' | 'ref-counted' | 'ownership'
    } = {lifetime: 'retained'};
}

export class Config {
    properties: Properties = {};
    network = new NetworkConfig();
    license: string
    readonly server: ServerConfig;
    readonly mesh: MeshConfig;
    readonly gateway: GatewayConfig;

    constructor() {
        this.server = new ServerConfig();
        this.mesh = new MeshConfig();
        this.gateway = new GatewayConfig();
    }

    get<T extends PropertyType>(name: string): T {
        const value = this.properties[name] as T;
        return value;
    }

    set<T extends PropertyType>(name: string, value: T): this {
        this.properties[name] = value;
        return this;
    }
}
